import { ExternalLink, X, Bookmark, BookmarkCheck, ThumbsUp, ThumbsDown, Eye, EyeOff, AlertTriangle, Newspaper, Share2, Link2, Mail, MessageCircle, Linkedin, Facebook, Sparkles, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScoreBadge } from "./ScoreBadge";
import { fmtDate, sentimentColor, templateMeta } from "../lib/format";
import { api } from "../lib/api";
import { toast } from "sonner";
import AskResultPopover from "./AskResultPopover";

const Lbl = ({ children }) => <div className="type-eyebrow text-[#64748B]">{children}</div>;

// Missing action_status predates the workflow — treat as open.
export function actionIsClosed(item) {
  return (item?.action_status || "open") === "closed";
}

export default function ResultModal({ item, project, open, onClose, isBookmarked, onToggleBookmark, onFeedback, isRead = false, onToggleRead, highlightAction = false, onActionStatusChange }) {
  const [fb, setFb] = useState(item?.feedback);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [resolvingShare, setResolvingShare] = useState(false);
  const navigate = useNavigate();
  const [askOpen, setAskOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [closingUi, setClosingUi] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [cpModalExpanded, setCpModalExpanded] = useState(true);
  const actionRef = useRef(null);
  useEffect(() => {
    setFb(item?.feedback);
    setShareOpen(false);
    setCopied(false);
    setShareUrl("");
    setResolvingShare(false);
    setActionBusy(false);
    setClosingUi(false);
    setCloseNote("");
  }, [item]);
  // When opened from the Dashboard's recommended-actions list, gently scroll
  // the action callout into view so the recommendation is front-and-centre.
  useEffect(() => {
    if (open && highlightAction && actionRef.current) {
      const t = setTimeout(() => actionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, highlightAction, item]);
  const setActionStatus = async (status, note) => {
    if (!item?.id || actionBusy) return;
    setActionBusy(true);
    try {
      const r = await api.post(`/results/${item.id}/action-status`, { status, note: note || null });
      const data = r.data || {};
      onActionStatusChange?.(item, data);
      if (status === "closed") {
        setClosingUi(false);
        setCloseNote("");
        toast.success(data.action_closed_note ? `Recommendation closed — "${data.action_closed_note}"` : "Recommendation closed");
      } else {
        toast.success("Recommendation reopened");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update recommendation");
    } finally {
      setActionBusy(false);
    }
  };
  const doFeedback = (val) => {
    const next = fb === val ? "clear" : val;
    setFb(next === "clear" ? null : next);
    onFeedback?.(item, next);
  };
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !item) return null;
  const tm = templateMeta(item.template_id);
  const alertLabel = item.alert_level ? item.alert_level.toUpperCase() : "";
  const alertColor = item.alert_level === "high" ? "#B91C1C" : item.alert_level === "medium" ? "#92400E" : "#64748B";

  const matched = item.matched_keywords || [];
  const entities = item.key_entities || [];
  const quotes = (item.key_quotes || []).filter(Boolean);
  const resolvedSourceUrl = Array.isArray(item.duplicate_sources)
    ? item.duplicate_sources
      .map((source) => source.url)
      .find((url) => url && !url.includes("news.google.com"))
    : null;
  const effectiveShareUrl = (shareUrl && !shareUrl.includes("news.google.com") ? shareUrl : null) || resolvedSourceUrl || (item.url && !item.url.includes("news.google.com") ? item.url : null) || shareUrl || item.url || window.location.href;
  const sources = Array.isArray(item.duplicate_sources) && item.duplicate_sources.length > 0
    ? item.duplicate_sources.map((source) => source.source || source.url).filter(Boolean)
    : [item.source].filter(Boolean);
  const shareText = [
    item.title,
    "",
    item.summary,
    "",
    `Sources: ${sources.join(", ") || "Original article"}`,
    effectiveShareUrl,
  ].filter(Boolean).join("\n");
  const shareChannels = [
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(shareText)}` },
    { label: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent(item.title)}&body=${encodeURIComponent(shareText)}` },
    { label: "SMS", icon: MessageCircle, href: `sms:?body=${encodeURIComponent(shareText)}` },
    { label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(effectiveShareUrl)}` },
    { label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(effectiveShareUrl)}` },
  ];

  const openShare = async () => {
    if (shareOpen) {
      setShareOpen(false);
      return;
    }
    const isGoogle = item.url?.includes("news.google.com");
    const isShareUrlResolved = shareUrl && !shareUrl.includes("news.google.com");
    if (!isGoogle || isShareUrlResolved || !item.id) {
      setShareOpen(true);
      return;
    }
    setResolvingShare(true);
    try {
      const response = await api.get(`/results/${item.id}/share-url`);
      if (response.data?.url) setShareUrl(response.data.url);
      setShareOpen(true);
    } finally {
      setResolvingShare(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="result-modal-overlay" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        role="dialog"
        aria-modal="true"
        data-testid="result-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-lg shadow-2xl w-full max-w-[720px] max-h-[90vh] overflow-y-auto"
      >
        {/* ROW 1 — Header */}
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-start gap-4 z-10">
          <ScoreBadge score={item.relevance_score} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-3">
              <span className="font-mono-tab font-bold text-[24px] leading-[28px]" style={{ color: alertColor, letterSpacing: "-0.02em" }}>
                {item.relevance_score}
              </span>
              <span className="type-eyebrow" style={{ color: alertColor }}>{alertLabel}</span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {project?.name && (
                <span className="type-eyebrow text-[#0F1F3D] bg-[#F1F5F9] px-2 py-0.5 rounded-md">{project.name}</span>
              )}
              <span className="type-eyebrow px-2 py-0.5 rounded-md" style={{ background: tm.color + "15", color: tm.color }}>{tm.label}</span>
              {item.category && (
                <span className="type-eyebrow px-2 py-0.5 rounded-md bg-[#F1F5F9] text-[#475569]">{item.category}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="result-modal-close"
            className="ml-2 p-2 rounded-md hover:bg-[#F1F5F9] text-[#64748B]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* ROW 2 — Title */}
          <h2 className="font-display text-[20px] leading-[28px] font-semibold text-[#0F1F3D]" style={{letterSpacing: "-0.01em"}} data-testid="result-modal-title">
            {item.title}
          </h2>

          {/* ROW 3 — Meta */}
          <div className="type-caption text-[#64748B] flex flex-wrap items-center gap-1.5">
            <span>{item.source}</span>
            {item.author && <><span>·</span><span>{item.author}</span></>}
            <span>·</span>
            <span>{fmtDate(item.published_at)}</span>
            {item.estimated_read_time && (<><span>·</span><span>{item.estimated_read_time}m read</span></>)}
            {item.sentiment && item.sentiment !== "neutral" && (
              <>
                <span>·</span>
                <span className="px-2 py-0.5 rounded-full type-eyebrow" style={{ background: sentimentColor(item.sentiment) + "1A", color: sentimentColor(item.sentiment) }}>
                  {item.sentiment}
                </span>
              </>
            )}
          </div>

          {/* ROW 4 — Summary */}
          <div>
            <Lbl>Summary</Lbl>
            <p className="mt-3 text-[15px] leading-[1.7] text-[#0F1F3D]" data-testid="result-modal-summary">{item.summary}</p>
          </div>

          {/* ROW 5 — Why this is relevant */}
          {item.ai_reasoning && (
            <div className="border-l-4 pl-4" style={{ borderColor: alertColor }}>
              <Lbl>Why this is relevant</Lbl>
              <p className="mt-3 text-[15px] leading-[1.7] text-[#0F1F3D]" data-testid="result-modal-reasoning">{item.ai_reasoning}</p>
            </div>
          )}

          {/* Cross-Project Relevance */}
          {item.cross_project_matches && item.cross_project_matches.length > 0 && (
            <div
              className="rounded-xl border border-[#DDD6FE] bg-[#FAF5FF]/60 p-4 transition-all"
              data-testid="result-modal-cross-project"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-6 w-6 rounded-full bg-[#EDE9FE] flex items-center justify-center shrink-0">
                    <Sparkles className="h-3.5 w-3.5 text-[#7C3AED]" />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#4C1D95]">
                      Cross-Project Relevance
                    </div>
                    <p className="text-xs text-[#64748B] mt-0.5">
                      This article may also impact these projects.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setCpModalExpanded(!cpModalExpanded)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#64748B] hover:text-[#0F1F3D] px-2 py-1 rounded hover:bg-[#EDE9FE]/50 transition-colors shrink-0"
                  data-testid="result-modal-cross-project-toggle"
                >
                  <span>
                    {item.cross_project_matches.length} related project{item.cross_project_matches.length > 1 ? "s" : ""}
                  </span>
                  {cpModalExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              </div>

              {cpModalExpanded && (
                <div className="mt-3.5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {item.cross_project_matches.map((match) => {
                    const conf = (match.confidence_level || "medium").toLowerCase();
                    const style =
                      conf === "high"
                        ? {
                            border: "border-emerald-200",
                            bg: "bg-emerald-50/20",
                            text: "text-emerald-700",
                            dot: "bg-emerald-500",
                            label: "High relevance",
                          }
                        : conf === "low"
                        ? {
                            border: "border-purple-200",
                            bg: "bg-purple-50/20",
                            text: "text-purple-700",
                            dot: "bg-purple-500",
                            label: "Low relevance",
                          }
                        : {
                            border: "border-blue-200",
                            bg: "bg-blue-50/20",
                            text: "text-blue-700",
                            dot: "bg-blue-500",
                            label: "Medium relevance",
                          };

                    return (
                      <div
                        key={match.project_id}
                        onClick={() => {
                          onClose();
                          navigate(`/app/feed?project_id=${match.project_id}`);
                        }}
                        className={`
                          group/modalitem rounded-lg border bg-white p-3 shadow-2xs hover:shadow-xs
                          hover:border-[#0F1F3D] transition-all cursor-pointer flex flex-col justify-between
                          ${style.border}
                        `}
                        data-testid={`modal-cross-project-match-${match.project_id}`}
                      >
                        <div>
                          <div className="text-sm font-semibold text-[#0F1F3D] truncate group-hover/modalitem:text-[#172A4F]">
                            {match.project_name}
                          </div>
                          <div className="mt-1 flex items-center gap-1.5">
                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${style.dot}`} />
                            <span className={`text-xs font-medium ${style.text}`}>
                              {style.label}
                            </span>
                          </div>
                        </div>
                        {match.reasoning && (
                          <p className="mt-2 text-xs text-[#64748B] line-clamp-3 leading-relaxed">
                            {match.reasoning}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Coverage — every outlet that covered this story (P1#4) */}
          {Array.isArray(item.duplicate_sources) && item.duplicate_sources.length > 1 && (
            <div data-testid="result-modal-coverage">
              <Lbl>Covered by {item.duplicate_sources.length} sources</Lbl>
              <ul className="mt-3 space-y-2">
                {item.duplicate_sources.map((s, i) => (
                  <li key={`${s.url}-${i}`} className="flex items-start gap-2 text-[14px]">
                    <Newspaper className="h-4 w-4 mt-0.5 text-[#6366F1] shrink-0" />
                    <div className="min-w-0">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#0F1F3D] font-medium hover:underline break-words"
                        data-testid={`result-modal-coverage-link-${i}`}
                      >
                        {s.source || s.url}
                      </a>
                      {s.title && <div className="text-[#64748B] text-[13px] break-words">{s.title}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}


          {/* P2: Recommended action — exposed for ANY result with a recommended_action,
              flagged with a stronger callout when action_required is true. When opened
              from the Dashboard recommended-actions list it is emphasised (ring).
              Shows open/closed status, the closing note when closed, and
              close (optional note) / reopen controls. */}
          {(item.recommended_action || item.action_note) && (
            <div
              ref={actionRef}
              className={`rounded-md border px-4 py-3 transition-shadow ${
                item.action_required
                  ? "border-[#FDE68A] bg-[#FFFBEB]"
                  : "border-[#E2E8F0] bg-[#F8FAFC]"
              } ${highlightAction ? "ring-2 ring-[#F59E0B] ring-offset-2" : ""}`}
              data-testid="result-modal-action"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <AlertTriangle
                  className={`h-4 w-4 ${item.action_required ? "text-[#B45309]" : "text-[#64748B]"}`}
                />
                <Lbl>
                  {item.action_required ? "Strategic recommendations" : "Strategic recommendations"}
                </Lbl>
                <span
                  data-testid="result-modal-action-status"
                  className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full type-eyebrow font-semibold ${
                    actionIsClosed(item)
                      ? "bg-[#F1F5F9] text-[#475569]"
                      : "bg-[#ECFDF5] text-[#047857]"
                  }`}
                >
                  {actionIsClosed(item) ? "Closed" : "Open"}
                </span>
                {item.action_confidence && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full type-eyebrow font-semibold bg-[#EFF6FF] text-[#1D4ED8]">
                    {item.action_confidence} confidence
                  </span>
                )}
              </div>
              <p className="mt-2 type-body text-[#0F1F3D]">
                {item.recommended_action || item.action_note}
              </p>
              {(item.action_why_now || item.action_first_step || item.action_owner_role) && (
                <div className="mt-3 space-y-3 border-t border-[#FDE68A] pt-3" data-testid="result-modal-action-details">
                  {item.action_why_now && (
                    <div>
                      <div className="type-eyebrow text-[#92400E]">Why now</div>
                      <p className="mt-1 type-body-sm text-[#475569]">{item.action_why_now}</p>
                    </div>
                  )}
                  {item.action_first_step && (
                    <div>
                      <div className="type-eyebrow text-[#92400E]">First step</div>
                      <p className="mt-1 type-body-sm text-[#475569]">{item.action_first_step}</p>
                    </div>
                  )}
                  {item.action_owner_role && (
                    <div>
                      <div>
                        <div className="type-eyebrow text-[#64748B]">Owner</div>
                        <div className="mt-1 type-body-sm text-[#0F1F3D]">{item.action_owner_role.replace(/_/g, " ")}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {actionIsClosed(item) && item.action_closed_note && (
                <p className="mt-2 type-body-sm text-[#475569] border-l-2 border-[#CBD5E1] pl-3" data-testid="result-modal-action-note">
                  Closing note: {item.action_closed_note}
                </p>
              )}
              {item.action_required && !actionIsClosed(item) && !closingUi && (
                <button
                  type="button"
                  onClick={() => setClosingUi(true)}
                  disabled={actionBusy}
                  data-testid="result-modal-action-close"
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E2E8F0] type-caption font-semibold text-[#475569] hover:bg-[#F1F5F9] disabled:opacity-50"
                >
                  Close recommendation
                </button>
              )}
              {item.action_required && !actionIsClosed(item) && closingUi && (
                <div className="mt-3 space-y-2" data-testid="result-modal-action-close-form">
                  <input
                    type="text"
                    value={closeNote}
                    onChange={(e) => setCloseNote(e.target.value)}
                    placeholder="Optional closing note…"
                    maxLength={280}
                    data-testid="result-modal-action-note-input"
                    className="w-full px-3 py-2 rounded-md border border-[#E2E8F0] type-body-sm text-[#0F1F3D] placeholder:text-[#94A3B8]"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActionStatus("closed", closeNote)}
                      disabled={actionBusy}
                      data-testid="result-modal-action-close-confirm"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#0F1F3D] text-white type-caption font-semibold hover:bg-[#172A4F] disabled:opacity-50"
                    >
                      {actionBusy ? "Closing…" : "Confirm close"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setClosingUi(false); setCloseNote(""); }}
                      disabled={actionBusy}
                      data-testid="result-modal-action-close-cancel"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E2E8F0] type-caption font-semibold text-[#475569] hover:bg-[#F1F5F9]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {item.action_required && actionIsClosed(item) && (
                <button
                  type="button"
                  onClick={() => setActionStatus("open")}
                  disabled={actionBusy}
                  data-testid="result-modal-action-reopen"
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E2E8F0] type-caption font-semibold text-[#475569] hover:bg-[#F1F5F9] disabled:opacity-50"
                >
                  {actionBusy ? "Working…" : "Reopen recommendation"}
                </button>
              )}
            </div>
          )}


          {/* ROW 6 — Matched keywords */}
          {matched.length > 0 && (
            <div>
              <Lbl>Matched keywords</Lbl>
              <div className="mt-3 flex flex-wrap gap-2">
                {matched.map((k) => (
                  <span key={k} className="px-2.5 py-1 rounded-full bg-[#F1F5F9] text-[#0F1F3D] type-caption">{k}</span>
                ))}
              </div>
            </div>
          )}

          {/* ROW 7 — Key entities */}
          {entities.length > 0 && (
            <div>
              <Lbl>Key entities mentioned</Lbl>
              <div className="mt-3 flex flex-wrap gap-2">
                {entities.map((e) => (
                  <span key={e} className="px-2.5 py-1 rounded-full bg-[#EEF2FF] text-[#3730A3] type-caption">{e}</span>
                ))}
              </div>
            </div>
          )}

          {/* ROW 8 — Notable quotes */}
          {quotes.length > 0 && (
            <div>
              <Lbl>Notable quotes</Lbl>
              <div className="mt-3 space-y-2">
                {quotes.map((q, i) => (
                  <blockquote key={`${i}-${q.slice(0, 12)}`} className="italic text-[#475569] type-body border-l-2 border-[#CBD5E1] pl-4">
                    “{q.replace(/^[“"']|[”"']$/g, "")}”
                  </blockquote>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ROW 10 — Footer */}
        <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={openShare}
                aria-expanded={shareOpen}
                aria-label="Share this item"
                data-testid="result-modal-share"
                disabled={resolvingShare}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E2E8F0] text-[#475569] type-caption font-semibold hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-wait"
              >
                {resolvingShare ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                {resolvingShare ? "Resolving..." : "Share"}
              </button>
              {shareOpen && (
                <div className="absolute bottom-full left-0 z-20 mb-2 w-52 rounded-md border border-[#E2E8F0] bg-white p-1.5 shadow-xl" data-testid="result-modal-share-menu">
                  {shareChannels.map(({ label, icon: Icon, href }) => (
                    <a
                      key={label}
                      href={href}
                      target={label === "Email" || label === "SMS" ? undefined : "_blank"}
                      rel={label === "Email" || label === "SMS" ? undefined : "noreferrer"}
                      onClick={() => setShareOpen(false)}
                      className="flex items-center gap-2 rounded px-2.5 py-2 text-sm text-[#0F1F3D] hover:bg-[#F1F5F9]"
                    >
                      <Icon className="h-4 w-4 text-[#64748B]" />
                      {label}
                    </a>
                  ))}
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-sm text-[#0F1F3D] hover:bg-[#F1F5F9]"
                  >
                    <Link2 className="h-4 w-4 text-[#64748B]" />
                    {copied ? "Copied" : "Copy link and summary"}
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => onToggleBookmark?.(item)} data-testid="result-modal-bookmark"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border type-caption font-semibold ${isBookmarked ? "bg-[#0F1F3D] text-white border-[#0F1F3D]" : "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"}`}>
              {isBookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              {isBookmarked ? "Saved" : "Save"}
            </button>
            <button onClick={() => doFeedback("up")} data-testid="result-modal-up"
              className={`p-2 rounded-md border transition-colors ${fb === "up" ? "bg-[#10B981] border-[#10B981] text-white" : "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"}`}>
              <ThumbsUp className="h-4 w-4" />
            </button>
            <button onClick={() => doFeedback("down")} data-testid="result-modal-down"
              className={`p-2 rounded-md border transition-colors ${fb === "down" ? "bg-[#EF4444] border-[#EF4444] text-white" : "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"}`}>
              <ThumbsDown className="h-4 w-4" />
            </button>
            {onToggleRead && (
              <button
                onClick={() => onToggleRead?.(item)}
                data-testid="result-modal-toggle-read"
                aria-label={isRead ? "Mark as unread" : "Mark as read"}
                title={isRead ? "Mark as unread" : "Mark as read"}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border type-caption font-semibold ${
                  isRead
                    ? "border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
                    : "border-[#0F1F3D] bg-[#0F1F3D] text-white hover:bg-[#172A4F]"
                }`}
              >
                {isRead ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {isRead ? "Mark unread" : "Read"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAskOpen(true)}
              data-testid="result-modal-ask-ai"
              aria-label="Ask AI about this article"
              title="Ask AI about this article"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-[#0F1F3D] text-white hover:bg-[#172A4F] transition-colors"
            >
              <Sparkles className="h-4 w-4" />
            </button>
            <a href={item.url} target="_blank" rel="noreferrer" data-testid="result-modal-open-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0F1F3D] text-white type-body-sm font-medium hover:bg-[#172A4F]">
              Read full article <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <AskResultPopover
        open={askOpen}
        onClose={() => setAskOpen(false)}
        projectName={project?.name}
        resultTitle={item.title}
        onSubmit={(q) => {
          setAskOpen(false);
          onClose();
          const params = new URLSearchParams({ q, project: item.project_id || "", resultId: item.id, new: "1" });
          navigate(`/app/ai-analysis?${params.toString()}`);
        }}
      />
    </div>
  );
}
